'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const subscribed = useRef(false);

  useEffect(() => {
    if (subscribed.current) return;
    subscribed.current = true;

    const supabase = createClient();

    // onAuthStateChange fires immediately with INITIAL_SESSION —
    // NO getSession() or getUser() needed (those cause rate limits)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const supabase = createClient();

  // Email/Password Sign Up with B.V. account creation
  const signUp = async (
    email: string,
    password: string,
    metadata: { fullName?: string; bvNaam?: string; kvkNummer?: string; btwNummer?: string } = {}
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          role: 'dga',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')}/auth/callback`,
      },
    });
    if (error) throw error;

    if (data.user && metadata.bvNaam && metadata.kvkNummer && metadata.btwNummer) {
      const { error: bvError } = await supabase.from('bv_accounts').insert({
        user_id: data.user.id,
        bv_naam: metadata.bvNaam,
        kvk_nummer: metadata.kvkNummer,
        btw_nummer: metadata.btwNummer,
      });
      if (bvError) console.error('BV account creation error:', bvError.message);
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // getCurrentUser leest uit state — GEEN API call naar Supabase!
  const getCurrentUser = () => {
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Get BV Accounts for current user
  const getBvAccounts = async () => {
    if (!user) return [];
    const { data, error } = await supabase
      .from('bv_accounts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    getBvAccounts,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:140:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="140" data-component-end-line="140" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
